import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {useAssignBulkActionPermission} from '../../../../../../srcCommon/permissions/useBulkActionPermission';
import {buildTeammateDisplayName} from '../../../../../helpers/modelHelpers';
import {findConversationNamespaces} from '../../../../../helpers/models/conversationHelpers';
import {ViewerDropdownInterfaceEventSourcesEnum} from '../../../../../helpers/models/tagHelpers';
import {PermissionDeniedReasonsEnum} from '../../../../../helpers/permissionHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {getHasLazyTeammatesDropdowns} from '../../../../../store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {TeammateAvatar} from '../../../../common/avatars/teammateAvatar';
import {PermissionPopoverCoordinator} from '../../../../common/popovers/permissionPopoverCoordinator';
import {AssignDropdownContainer} from './assign/assignDropdownContainer';
import {LazyAssignDropdownContainer} from './assign/lazyAssignDropdownContainer';

/*
 * Props.
 */

interface ConversationTopbarAssignProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  assigneeAll?: TeammateModel;
  inboxesParameters: InboxesParameters;
  isBulkActionRequired: boolean;
}

/*
 * Style.
 */

const StyledAssigneeDiv = styled.div`
  display: flex;
  align-items: center;
  margin: -3px -5px -4px -10px;
`;

const StyledAssignDiv = styled(StyledAssigneeDiv)`
  margin: 0;
  margin-right: -5px;
`;

const StyledNameDiv = styled.div`
  margin: -1px 3px 0 5px;
  max-width: 120px;
  ${ellipsis()};
`;

const StyledAssignTextDiv = styled.div`
  max-width: 120px;
  ${ellipsis()};
  margin-right: 5px;
  margin-left: 1px;
`;

/*
 * Component.
 */

export const ConversationTopbarAssign: FC<ConversationTopbarAssignProps> = (props) => {
  const {conversations, assigneeAll, isBulkActionRequired, inboxesParameters} = props;
  const bulkActionPermission = useAssignBulkActionPermission(isBulkActionRequired, inboxesParameters);
  const hasLazyTeammatesDropdowns = useAppSelector(getHasLazyTeammatesDropdowns);

  return (
    <PermissionPopoverCoordinator
      namespaces={findConversationNamespaces(conversations)}
      permission={PermissionsEnum.CONVERSATION_ASSIGN}
      renderButton={(isOpen) => renderButton(isOpen, assigneeAll)}
      renderDisabledButton={(reason) => renderNoAccessButton(reason, assigneeAll)}
      renderModal={(modalProps) => renderDropdown(modalProps, props, hasLazyTeammatesDropdowns)}
      keyboardAction={KeyboardActionsEnum.ASSIGN}
      bulkActionPermission={bulkActionPermission}
    />
  );
};

/*
 * Button.
 */

function renderButton(isOpen: boolean, assigneeAll?: TeammateModel) {
  return (
    <Button hasHover={isOpen} interactionId="conversation-topbar-assign">
      <TooltipCoordinator render={() => maybeRenderTooltip(assigneeAll)}>
        {renderButtonContent(assigneeAll)}
      </TooltipCoordinator>
    </Button>
  );
}

function renderNoAccessButton(reason: PermissionDeniedReasonsEnum, assigneeAll?: TeammateModel) {
  return (
    <TooltipCoordinator render={() => <PermissionDenied reason={reason} />}>
      <Button isDisabled>{renderButtonContent(assigneeAll)}</Button>
    </TooltipCoordinator>
  );
}

function maybeRenderTooltip(assigneeAll?: TeammateModel) {
  if (!assigneeAll) {
    return null;
  }

  return <Trans id="conversation-topbar-assign-tooltip">Assignee</Trans>;
}

function renderButtonContent(assigneeAll?: TeammateModel) {
  // If we don't have an assignee, just render the button.
  if (!assigneeAll) {
    return (
      <StyledAssignDiv>
        <StyledAssignTextDiv>
          <Trans id="conversation-topbar-assign">Assign</Trans>
        </StyledAssignTextDiv>
      </StyledAssignDiv>
    );
  }

  // Otherwise, render the assignee.
  return (
    <StyledAssigneeDiv>
      <TeammateAvatar teammate={assigneeAll} size={24} />
      <StyledNameDiv>{buildTeammateDisplayName(assigneeAll)}</StyledNameDiv>
      <Icon name="chevronDownSmall" />
    </StyledAssigneeDiv>
  );
}

/*
 * Dropdown.
 */

function renderDropdown(
  modalProps: PopoverTransferProps,
  props: ConversationTopbarAssignProps,
  hasLazyTeammatesDropdowns: boolean,
) {
  if (hasLazyTeammatesDropdowns) {
    return (
      <LazyAssignDropdownContainer
        {...modalProps}
        placement="bottom-end"
        cellsContext={props.cellsContext}
        conversations={props.conversations}
        isBulkActionRequired={props.isBulkActionRequired}
        inboxesParameters={props.inboxesParameters}
        interfaceEventSource={ViewerDropdownInterfaceEventSourcesEnum.TOPBAR}
        panelId={props.panelId}
      />
    );
  }
  return (
    <AssignDropdownContainer
      {...modalProps}
      placement="bottom-end"
      cellsContext={props.cellsContext}
      conversations={props.conversations}
      isBulkActionRequired={props.isBulkActionRequired}
      inboxesParameters={props.inboxesParameters}
      interfaceEventSource={ViewerDropdownInterfaceEventSourcesEnum.TOPBAR}
      panelId={props.panelId}
    />
  );
}
